import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate, useParams} from 'react-router-dom';
import {Button, Dialog, Input, NavBar, Toast} from 'antd-mobile';
import { getNavBarContainerStyle, getBottomNavBarContainerStyle } from "../../../utils/deviceUtils";
import {PhoneFill} from 'antd-mobile-icons';
import EmptyState from "../../../components/EmptyState";
import {useTranslation} from "react-i18next";
import {NativePermissions} from "../../delivery/components/PhotoNativeBridge";
import {pickupNavigationService} from "../services/PickupNavigationService";
import {PickupAppointmentType} from "../graphql/types/PickupTaskWithAppointmentsType";
import {usePickupTaskAppointment, usePickupTaskOperations} from "../store/pickupStore";
import {buildAddressFromAppointment} from "../../../utils/addressUtils";
import {getBusinessDaysText, getPickupFailureReasonText, isOnsiteContactApplicable} from "../../../enums/PickupEnum";
import {formatTimestampByLanguage} from "../../../utils/dateTimeUtils";
import pickedUpDataService, {PickedUpData, uploadPhotosWithTimeout} from "../../../services/PickedUpDataService";
import pickUpPhotoService, {PickUpPhotoType, PickUpPositionType} from "../services/PickUpPhotoService";
import geolocationService from "../services/PickUpGeolocationService";
import PickUpPhotoUploadCard from '../components/PickUpPhotoUploadCard';
import PickUpPhotoViewer from '../components/PickUpPhotoViewer';
import dayjs from "dayjs";
import PickUpInfoItem from "../components/PickUpInfoItem";
import {parseExceptionMessageKey} from "../../../utils/exceptionUtils";
import {navigateBack} from "../../../components/Map/MapNavigationUtils";
import {usePickupSiteV2Gray} from "../../../hooks/usePickupSiteV2Gray";

interface MarkExceptionProps {
}

const PickupFailedPage: React.FC<MarkExceptionProps> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { appointmentId, failedReason } = useParams<{
        appointmentId: string;
        failedReason: string;
    }>();
    // 检查是否来自地图模式
    const fromMapMode = location.state?.fromMapMode || false;
    const {t, i18n} = useTranslation();
    const [photos, setPhotos] = useState<Record<PickUpPositionType, PickUpPhotoType | undefined>>({} as Record<PickUpPositionType, PickUpPhotoType | undefined>);
    const [viewingPhoto, setViewingPhoto] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pickUpDetail, setPickUpDetail] = useState<PickupAppointmentType>();
    const [nextItem, setNextItem] = useState<PickupAppointmentType | null>(null);
    const {pickupTaskAppointment, loading} = usePickupTaskAppointment(appointmentId || '');
    const {handlePickupFailed, submitPickupOnsiteFeedback} = usePickupTaskOperations();

    // 揽收站点 2.0：揽收失败详情新增「现场联系人信息」模块（灰度 hub 展示；非必填）
    const { express2Enabled } = usePickupSiteV2Gray();
    // 按 hub 门控：仅当前 appointment 所属 hub 在灰度名单内才启用 2.0（避免"任一 hub 灰度即全量"）
    const siteV2Enabled = express2Enabled(pickUpDetail?.warehouseId);
    // 建联状态门控口径与揽收成功页一致：只对待上门/上门中/揽收成功待建联/揽收失败待建联的站点采集，
    // 已建联(4)/确认无法建联(5)/非灰度(null) 不展示
    const showOnSiteContact = siteV2Enabled && isOnsiteContactApplicable(pickUpDetail?.linkStatus);
    const [onSiteContactName, setOnSiteContactName] = useState<string>('');
    const [onSiteContactPhone, setOnSiteContactPhone] = useState<string>('');

    useEffect(() => {
        if (pickupTaskAppointment) {
            setPickUpDetail(pickupTaskAppointment);
            const currentStopNumber = pickUpDetail?.sequence;
            if (currentStopNumber && !nextItem) {
                pickupNavigationService.getNextPickupItem(currentStopNumber).then(setNextItem);
            }
        }
    }, [pickupTaskAppointment, pickUpDetail?.sequence]);


    useEffect(() => {
        const requestPermissions = async () => {
            try {
                await NativePermissions.request('camera');
                await NativePermissions.request('storage');
            } catch (error) {
                console.error('Failed to request permissions:', error);
            }
        };

        requestPermissions();
    }, []);

    /*照片相关操作 start======*/

    // 拍照或从相册选择
    const takePhoto = async (type: PickUpPositionType, file: File) => {
        try {
            setIsLoading(true);
            await pickUpPhotoService.handlePhotoNotUpload(type, file, (updateFn) => {
                setPhotos((prev) => updateFn(prev));
            });
        } catch (error) {
            console.error('Failed to take photo:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // 删除照片
    const deletePhoto = (type: PickUpPositionType) => {
        Dialog.confirm({
            content: t('pages.pickUp.photo.deleteContent'),
            confirmText: t('pages.pickUp.photo.deleteConfirm'),
            cancelText: t('pages.pickUp.photo.deleteCancel'),
            onConfirm: () => {
                pickUpPhotoService.handleDeletePhoto(type, (updateFn) => {
                    setPhotos((prev) => updateFn(prev));
                });
            },
        });
    };

    // 查看大图
    const handleViewPhoto = (url: string) => setViewingPhoto(url);

    /*照片相关操作 end======*/

    // 保存订单数据到本地
    const savePickedUpToLocal = async (): Promise<boolean> => {
        try {
            const pickedUpData: PickedUpData = {
                updatedAt: Date.now(),
                photos: Object.entries(photos || {})
                    .map(([type, photo]) => ({
                        type: type as PickUpPositionType,
                        photo: photo as PickUpPhotoType
                    })),
                id: pickUpDetail?.id?.toString() || '',
                type: 'failed',
                stopNumber: pickUpDetail?.sequence?.toString(),
                channelName: pickUpDetail?.channelName,
                taskId: pickUpDetail?.taskId || '',
                failedReason: failedReason || '',
            };

            // 保存到本地
            const saved = await pickedUpDataService.savePickedUpData(pickedUpData);
            return saved;
        } catch (error) {
            console.error('Failed to save PickedUp to local:', error);
            return false;
        }
    };

    // 处理揽收失败
    const handlePickupFailedMutation = async () => {
        setIsLoading(true);
        try {
            if (!photos.signFor?.objectKey || !photos.packageImg?.objectKey || !photos.warehousePhoto?.objectKey) {
                Toast.show({content: t('pages.pickUp.finished.toast.needPhotos'), position: 'center'});
                return;
            }
            // 揽收站点 2.0：现场联系方式非必填，但填了必须 10 位（合规校验）→ 拦在 1.0 落库前
            if (showOnSiteContact && onSiteContactPhone !== '' && onSiteContactPhone.length !== 10) {
                Toast.show({ content: t('pages.pickUp.onSiteContact.phoneInvalid'), position: 'center' });
                setIsLoading(false);
                return;
            }

            // 揽收失败动作时刻取一次实时定位（供 POP 审核判揽收点与站点距离）；
            // 取位失败/无权限则留空，不写 0.0、不阻断提交（后端据空值识别"无定位"）。
            let pickupLatitude: number | undefined;
            let pickupLongitude: number | undefined;
            try {
                const pos = await geolocationService.getFreshPosition();
                pickupLatitude = pos.latitude;
                pickupLongitude = pos.longitude;
                console.log('[PickupFailed] 取位成功', pickupLatitude, pickupLongitude);
            } catch (e) {
                console.warn('[PickupFailed] 取位失败，经纬度留空提交', e);
            }

            const currentPhotos = {...photos};
            await handlePickupFailed({
                appointmentId: pickUpDetail?.id || '',
                taskId: pickUpDetail?.taskId || '',
                failedReason: failedReason || '',
                exceptionPhotosS3KeysJson: [photos?.signFor?.objectKey, photos?.packageImg?.objectKey,photos?.warehousePhoto?.objectKey],
                actualPickupDate:dayjs().format("YYYY-MM-DD"),
                actualPickupTimeEnd:dayjs().format("HH:mm:ss"),
                cooperationWarehouseId: pickUpDetail?.cooperationWarehouseId,
                latitude: pickupLatitude,
                longitude: pickupLongitude,
            }, true);

            setPhotos(currentPhotos);
            // 图片上传，带超时控制
            const photoKeys = await uploadPhotosWithTimeout(
                Object.values(photos)
                    .filter((item): item is PickUpPhotoType => item?.objectKey !== undefined && item?.url !== undefined)
            );

            await handlePickupFailed({
                appointmentId: pickUpDetail?.id || '',
                taskId: pickUpDetail?.taskId || '',
                failedReason: failedReason || '',
                exceptionPhotosS3KeysJson: photoKeys,
                actualPickupDate:dayjs().format("YYYY-MM-DD"),
                actualPickupTimeEnd:dayjs().format("HH:mm:ss"),
                cooperationWarehouseId: pickUpDetail?.cooperationWarehouseId,
                latitude: pickupLatitude,
                longitude: pickupLongitude,
            }, false);

            // 揽收站点 2.0 灰度 hub：追加现场回传（现场联系人非必填 + 推站点建联状态）。
            // best-effort：失败结果已由上面的 1.0 通路落库，这里失败不回滚、不阻断主流程，仅 toast 提示。
            if (siteV2Enabled && pickUpDetail?.id) {
                try {
                    await submitPickupOnsiteFeedback({
                        pickupAppointmentId: pickUpDetail.id,
                        pickupSuccess: false,
                        onsiteContactName: onSiteContactName.trim(),
                        onsiteContactPhone: onSiteContactPhone,
                        photosS3KeysJson: JSON.stringify(photoKeys),
                        // 上门备注：揽收失败复用失败原因（→ op5 content.note）
                        note: failedReason || undefined,
                    });
                } catch (feedbackError) {
                    console.error('submitPickupOnsiteFeedback failed (non-blocking):', feedbackError);
                    Toast.show({ content: t('pages.pickUp.onSiteContact.submitFailed'), position: 'center' });
                }
            }

            console.log('History length:', window.history.length);
            console.log('Current pathname:', window.location.pathname);
            console.log('fromMapMode:', fromMapMode);
            if (nextItem && !fromMapMode) {
                console.log('详情下一条数据');
                navigate(`/pickup/picking-up/detail/${nextItem.id}/${nextItem.status}`);
            } else if (fromMapMode) {
                console.log('返回到地图');
                navigateBack.navigateToLsatPage(navigate);
            } else {
                console.log('返回到列表');
                navigateBack.navigateToPickupList(navigate);
            }
        } catch (error: any) {
            if (error.networkError) {
                await savePickedUpToLocal();
                if (nextItem && !fromMapMode) {
                    navigate(`/pickup/picking-up/detail/${nextItem.id}/${nextItem.status}`);
                } else if (fromMapMode) {
                    navigateBack.navigateToLsatPage(navigate);
                } else {
                    navigateBack.navigateToPickupList(navigate);
                }
            } else {
                console.error('Failed to complete PickupFailedPage:', error);
                const messageKey = parseExceptionMessageKey(error);
                if (messageKey === 'errors.pickup.cancelledAppointmentDotUpdate'){
                    Toast.show({ content: t(messageKey), position: 'center' });
                }else {
                    Toast.show({content: 'Mark failed, please try again', position: 'center'});
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    // 拨打电话
    const handleCall = () => {
        if (!pickUpDetail?.contactPhone || pickUpDetail.contactPhone.trim() === '') {
            Toast.show({
                content: t('pages.delivery.orderDetail.noPhoneNumber'),
                position: 'bottom',
            });
            return;
        }

        // 清理电话号码，只保留数字和+号
        const cleanPhone = pickUpDetail.contactPhone.replace(/[^\d+]/g, '');

        if (!cleanPhone) {
            Toast.show({
                content: t('pages.delivery.orderDetail.invalidPhoneNumber'),
                position: 'bottom',
            });
            return;
        }

        try {
            window.location.href = `tel:${cleanPhone}`;
        } catch (error) {
            console.error('Phone call failed:', error);
            Toast.show({
                content: t('pages.delivery.orderDetail.phoneCallFailed', {phone: cleanPhone}),
                position: 'bottom',
                duration: 3000
            });
        }
    };

    if (!pickUpDetail) {
        return (
            <div className="flex flex-col h-screen">
                <div {...getNavBarContainerStyle()}>
                    <NavBar onBack={() => navigate(-1)}>
                        {t('pages.pickUp.pickUpFailed.title')}
                    </NavBar>
                </div>
                <EmptyState/>
            </div>
        );
    }
    const businessDaysStr = getBusinessDaysText(pickUpDetail.businessDays, i18n.language);
    return (
        <div className="flex flex-col h-screen">
            <div {...getNavBarContainerStyle()}>
                <NavBar onBack={() => navigate(-1)}>{t('pages.pickUp.pickUpFailed.title')}</NavBar>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="mb-1 bg-white p-4">
                    <h2 className="text-base font-semibold mb-1">{t('pages.pickUp.pickUpFailed.detailInformation')}</h2>
                    <div className="mb-1">
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.name')} value={pickUpDetail.channelName}/>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.number')}
                                  value={pickUpDetail?.sequence?.toString()}/>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.contact')} value={pickUpDetail?.contactName}/>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.phone')}
                                  value={`${pickUpDetail?.contactPhone}${pickUpDetail?.ext ? `-${pickUpDetail?.ext}` : ''}`}/>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.address')}
                                  value={buildAddressFromAppointment(pickUpDetail)}/>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.businessHours')} value={businessDaysStr}/>
                        <div className="flex justify-between py-2 border-gray-100">
                            <span className="text-gray-600 text-sm"></span>
                            <span className="text-sm font-medium text-right">{pickUpDetail?.businessHours}</span>
                        </div>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.appointmentTime')}
                                  value={formatTimestampByLanguage(pickUpDetail?.appointmentTime, i18n.language)}/>
                        <div className="flex justify-between py-2 border-gray-100">
                            <span className="text-gray-600 text-sm">{t('pages.pickUp.pickUpFailed.failedReason')}:</span>
                            <span
                                className="text-sm font-medium text-right text-[#6366f1]">{getPickupFailureReasonText(failedReason || '', i18n.language)}</span>
                        </div>
                        <PickUpInfoItem label={t('pages.pickUp.pickUpFailed.remark')} value={pickUpDetail?.remark || '-'} />
                    </div>
                </div>

                {/* 现场联系人信息（揽收站点 2.0 · 揽收失败新增模块，灰度 hub + 建联流程中的站点展示，非必填） */}
                {showOnSiteContact && (
                    <div className="mb-1 bg-white p-4 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                            <h2 className="text-base font-semibold">{t('pages.pickUp.onSiteContact.title')}</h2>
                            <span className="text-xs text-green-600 bg-green-50 rounded px-1.5 py-0.5">
                                {t('pages.pickUp.onSiteContact.new')}
                            </span>
                        </div>
                        <div className="mb-2">
                            <div className="text-sm mb-1">{t('pages.pickUp.onSiteContact.contactName')}</div>
                            <Input
                                placeholder={t('pages.pickUp.onSiteContact.contactNamePlaceholder') as string}
                                value={onSiteContactName}
                                onChange={setOnSiteContactName}
                                className="border border-gray-300 rounded-md h-10 px-2"
                            />
                        </div>
                        <div>
                            <div className="text-sm mb-1">{t('pages.pickUp.onSiteContact.contactPhone')}</div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-600 border border-gray-300 rounded-md h-10 flex items-center px-2">+1</span>
                                <Input
                                    placeholder={t('pages.pickUp.onSiteContact.contactPhonePlaceholder') as string}
                                    value={onSiteContactPhone}
                                    onChange={(v) => setOnSiteContactPhone(v.replace(/\D/g, '').slice(0, 10))}
                                    type="tel"
                                    className="border border-gray-300 rounded-md h-10 px-2 flex-1"
                                />
                            </div>
                            {onSiteContactPhone !== '' && onSiteContactPhone.length !== 10 && (
                                <div className="text-xs text-red-500 mt-1">{t('pages.pickUp.onSiteContact.phoneInvalid')}</div>
                            )}
                        </div>
                    </div>
                )}

                <div className="mb-2 bg-white p-4 border-t-1 shadow-sm">
                    <h2 className="text-base font-semibold mb-1">{t('pages.pickUp.pickUpFailed.photos')}</h2>
                    <p className="text-sm text-gray-600 mb-1">
                        {t('pages.pickUp.pickUpFailed.photoDesc')}
                        <span className="text-green-600 text-base">{t('pages.pickUp.pickUpFailed.photoDescDetail')}</span>
                        {t('pages.pickUp.pickUpFailed.punctuation')}
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                        <PickUpPhotoUploadCard
                            photoUrl={photos.signFor?.url}
                            type="signFor"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                        />
                        <PickUpPhotoUploadCard
                            photoUrl={photos.packageImg?.url}
                            type="packageImg"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                        />
                        <PickUpPhotoUploadCard
                            photoUrl={photos.warehousePhoto?.url}
                            type="warehousePhoto"
                            description=""
                            onTakePhoto={takePhoto}
                            onDelete={deletePhoto}
                            onView={handleViewPhoto}
                        />
                    </div>
                </div>

                {/* 查看大图 */}
                <PickUpPhotoViewer
                    photoUrl={viewingPhoto}
                    onClose={() => setViewingPhoto(null)}
                />
            </div>

            <div {...getBottomNavBarContainerStyle('flex-0 flex px-4 py-2 mt-auto gap-4')}>
                <div className="flex-0">
                    <Button
                        shape="rounded"
                        className="w-12 h-12 flex items-center justify-center"
                        onClick={handleCall}
                        style={{'--background-color': '#f0f0f0'}}
                    >
                        <PhoneFill className="text-xl"/>
                    </Button>
                </div>
                <div className="flex-1">
                    <Button
                        block
                        color="primary"
                        loading={isLoading}
                        disabled={!photos.signFor?.url || !photos.packageImg?.url ||!photos.warehousePhoto?.url || isLoading}
                        onClick={handlePickupFailedMutation}
                    >
                        {t('pages.pickUp.pickUpFailed.complete')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default PickupFailedPage;
